import {
  formatDecimal,
  formatInteger as formatLocaleInteger,
} from "@/i18n/runtime";
import { huge, type HugeNumber, isHugeNumber } from "@/shared/numbers";

export type Notation = "standard" | "scientific" | "engineering";

export type FormattableNumber = number | HugeNumber;

export interface NumberFormatPolicy {
  readonly notation: Notation;
  readonly compactThreshold: number;
  readonly decimals: number;
  readonly trimTrailingZeros: boolean;
  // Player-facing stepped digit rule applied AFTER the suffix/notation split:
  //   1 <= |m| < 10   -> up to 2 decimals
  //   10 <= |m| < 100 -> up to 1 decimal
  //   100 <= |m|      -> 0 decimals
  // Player-facing contexts (main, compact, tooltip) use this so suffix and
  // scientific outputs stay around three significant digits (e.g. 397NQd).
  readonly mantissaStepped: boolean;
  // Apply the same stepped rule to values rendered BEFORE the suffix split,
  // via the locale fallback path (e.g. "21,647" instead of "21,647.35").
  // Player-facing contexts (main, compact, tooltip) enable this for visibility;
  // detail and debug keep fixed decimals.
  readonly localeFallbackStepped: boolean;
}

export type FormatContextId =
  | "main"
  | "compact"
  | "tooltip"
  | "detail"
  | "debug";

export function isNotation(value: unknown): value is Notation {
  return (
    value === "standard" || value === "scientific" || value === "engineering"
  );
}

const DEFAULT_NOTATION: Notation = "standard";

let currentNotation: Notation = DEFAULT_NOTATION;

export function getCurrentNotation(): Notation {
  return currentNotation;
}

export function setCurrentNotation(notation: Notation): void {
  currentNotation = notation;
}

const STANDARD_SUFFIXES: readonly string[] = [
  "M",
  "B",
  "T",
  "Qa",
  "Qi",
  "Sx",
  "Sp",
  "Oc",
  "No",
  "Dc",
  "UDc",
  "DDc",
  "TDc",
  "QaDc",
  "QiDc",
  "SxDc",
  "SpDc",
  "ODc",
  "NDc",
  "Vg",
  "UVg",
  "DVg",
  "TVg",
  "QaVg",
  "QiVg",
  "SxVg",
  "SpVg",
  "OVg",
  "NVg",
  "Tg",
  "UTg",
  "DTg",
  "TTg",
  "QaTg",
  "QiTg",
  "SxTg",
  "SpTg",
  "OTg",
  "NTg",
  "Qd",
  "UQd",
  "DQd",
  "TQd",
  "QaQd",
  "QiQd",
  "SxQd",
  "SpQd",
  "OQd",
  "NQd",
  "Qq",
  "UQq",
  "DQq",
  "TQq",
  "QaQq",
  "QiQq",
  "SxQq",
  "SpQq",
  "OQq",
  "NQq",
  "Sg",
  "USg",
  "DSg",
  "TSg",
  "QaSg",
  "QiSg",
  "SxSg",
  "SpSg",
  "OSg",
  "NSg",
  "St",
  "USt",
  "DSt",
  "TSt",
  "QaSt",
  "QiSt",
  "SxSt",
  "SpSt",
  "OSt",
  "NSt",
  "Og",
  "UOg",
  "DOg",
  "TOg",
  "QaOg",
  "QiOg",
  "SxOg",
  "SpOg",
  "OOg",
  "NOg",
  "Ng",
  "UNg",
  "DNg",
  "TNg",
  "QaNg",
  "QiNg",
  "SxNg",
  "SpNg",
  "ONg",
  "NNg",
];

export const STANDARD_SUFFIX_SCIENTIFIC_FALLBACK_THRESHOLD = Math.pow(
  10,
  (STANDARD_SUFFIXES.length + 2) * 3,
);

const CONTEXT_DEFAULTS: Readonly<Record<FormatContextId, NumberFormatPolicy>> =
  {
    main: {
      notation: DEFAULT_NOTATION,
      compactThreshold: 1e9,
      decimals: 2,
      trimTrailingZeros: true,
      mantissaStepped: true,
      localeFallbackStepped: true,
    },
    compact: {
      notation: DEFAULT_NOTATION,
      compactThreshold: 1e6,
      decimals: 2,
      trimTrailingZeros: true,
      mantissaStepped: true,
      localeFallbackStepped: true,
    },
    tooltip: {
      notation: DEFAULT_NOTATION,
      compactThreshold: 1e9,
      decimals: 3,
      trimTrailingZeros: true,
      mantissaStepped: true,
      localeFallbackStepped: true,
    },
    // Player-facing high-precision context. Reserved for future surfaces that
    // need more than the stepped 3-digit mantissa (e.g. showing slow movement
    // in stalled regions). Not wired to existing UI; opt-in only.
    detail: {
      notation: DEFAULT_NOTATION,
      compactThreshold: 1e9,
      decimals: 3,
      trimTrailingZeros: true,
      mantissaStepped: false,
      localeFallbackStepped: false,
    },
    debug: {
      notation: "scientific",
      compactThreshold: 0,
      decimals: 3,
      trimTrailingZeros: false,
      mantissaStepped: false,
      localeFallbackStepped: false,
    },
  };

export function resolvePolicy(
  context: FormatContextId,
  notation: Notation,
  overrides?: Partial<NumberFormatPolicy>,
): NumberFormatPolicy {
  const base = CONTEXT_DEFAULTS[context];
  const effectiveNotation = context === "debug" ? base.notation : notation;
  return {
    notation: effectiveNotation,
    compactThreshold: base.compactThreshold,
    decimals: base.decimals,
    trimTrailingZeros: base.trimTrailingZeros,
    mantissaStepped: base.mantissaStepped,
    localeFallbackStepped: base.localeFallbackStepped,
    ...overrides,
  };
}

function formatExtendedNumberWithPolicy(
  value: HugeNumber,
  policy: NumberFormatPolicy,
): string {
  if (!value.isFinite()) {
    if (value.isNan()) return "NaN";
    return value.gt(0) ? "∞" : "-∞";
  }

  const absValue = value.abs();
  const sign = value.lt(0) ? "-" : "";

  if (absValue.lt(1e9)) {
    return sign + formatFiniteNumberWithPolicy(absValue.toNumber(), policy);
  }

  if (policy.notation === "engineering") {
    return sign + formatEngineeringHugeValue(absValue, policy);
  }

  if (policy.notation === "standard") {
    return sign + formatStandardSuffixHugeValue(absValue, policy);
  }

  return sign + formatScientificHugeValue(absValue, policy);
}

export function formatFiniteNumberWithPolicy(
  value: number,
  policy: NumberFormatPolicy,
): string {
  if (!Number.isFinite(value)) {
    if (Number.isNaN(value)) return "NaN";
    return value > 0 ? "∞" : "-∞";
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (policy.notation === "scientific") {
    if (absValue !== 0 && absValue < policy.compactThreshold) {
      return sign + formatLocaleFallback(absValue, policy);
    }
    return sign + formatScientific(absValue, policy);
  }

  if (policy.notation === "engineering") {
    return sign + formatEngineering(absValue, policy);
  }

  // standard
  if (absValue < policy.compactThreshold) {
    return sign + formatLocaleFallback(absValue, policy);
  }
  if (absValue >= STANDARD_SUFFIX_SCIENTIFIC_FALLBACK_THRESHOLD) {
    return sign + formatScientific(absValue, policy);
  }
  return sign + formatStandardSuffix(absValue, policy);
}

export function formatNumber(
  value: FormattableNumber,
  policy: NumberFormatPolicy,
): string {
  if (isHugeNumber(value)) {
    return formatExtendedNumberWithPolicy(value, policy);
  }

  return formatFiniteNumberWithPolicy(value, policy);
}

export function formatForContext(
  value: FormattableNumber,
  context: FormatContextId,
  notation: Notation = currentNotation,
  overrides?: Partial<NumberFormatPolicy>,
): string {
  if (isZeroValue(value)) {
    return "0";
  }

  return formatNumber(value, resolvePolicy(context, notation, overrides));
}

export function formatPercent(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return "—";
  return `${formatDecimal(value, decimals)}%`;
}

export function formatMultiplier(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return "—";
  return `x${formatDecimal(value, decimals)}`;
}

function isZeroValue(value: FormattableNumber): boolean {
  if (isHugeNumber(value)) {
    return value.eq(0);
  }

  return Object.is(value, -0) || value === 0;
}

function formatStandardSuffix(
  absValue: number,
  policy: NumberFormatPolicy,
): string {
  const exponent = Math.floor(Math.log10(absValue));
  const tierIndex = Math.floor(exponent / 3) - 2; // 1e6 → 0
  if (tierIndex < 0) {
    return formatScientific(absValue, policy);
  }
  const clamped = Math.min(tierIndex, STANDARD_SUFFIXES.length - 1);
  const suffix = STANDARD_SUFFIXES[clamped];
  const tierExponent = (clamped + 2) * 3;
  const mantissa = absValue / Math.pow(10, tierExponent);
  return `${formatMantissa(mantissa, policy)}${suffix}`;
}

function formatScientific(
  absValue: number,
  policy: NumberFormatPolicy,
): string {
  if (absValue === 0) return formatMantissa(0, policy);
  const exponent = Math.floor(Math.log10(absValue));
  const mantissa = absValue / Math.pow(10, exponent);
  return `${formatMantissa(mantissa, policy)}e${exponent}`;
}

function formatEngineering(
  absValue: number,
  policy: NumberFormatPolicy,
): string {
  if (absValue === 0) return formatMantissa(0, policy);
  const rawExponent = Math.floor(Math.log10(absValue));
  const engExponent = Math.floor(rawExponent / 3) * 3;
  const mantissa = absValue / Math.pow(10, engExponent);
  if (absValue < policy.compactThreshold) {
    return formatLocaleFallback(absValue, policy);
  }
  return `${formatMantissa(mantissa, policy)}e${engExponent}`;
}

function formatScientificHugeValue(
  absValue: HugeNumber,
  policy: NumberFormatPolicy,
): string {
  // Decompose into mantissa + exponent through HugeNumber to avoid overflow.
  const rawExponent = Math.floor(absValue.log10().toNumber());
  if (Number.isFinite(rawExponent)) {
    const mantissa = absValue.div(huge(`1e${rawExponent}`)).toNumber();
    if (Number.isFinite(mantissa)) {
      return `${formatMantissa(mantissa, policy)}e${rawExponent}`;
    }
  }
  // Fallback to the native exponential path if log10/div lose precision.
  const formatted = absValue.toExponential(policy.decimals).replace("e+", "e");
  if (!policy.trimTrailingZeros) return formatted;
  const exponentIndex = formatted.lastIndexOf("e");
  if (exponentIndex <= 0) return formatted;
  const mantissa = formatted.slice(0, exponentIndex).replace(/\.?0+$/, "");
  return `${mantissa}${formatted.slice(exponentIndex)}`;
}

function formatEngineeringHugeValue(
  absValue: HugeNumber,
  policy: NumberFormatPolicy,
): string {
  const rawExponent = Math.floor(absValue.log10().toNumber());
  if (!Number.isFinite(rawExponent)) {
    return formatScientificHugeValue(absValue, policy);
  }
  const engineeringExponent = Math.floor(rawExponent / 3) * 3;
  const mantissa = absValue.div(huge(`1e${engineeringExponent}`)).toNumber();
  if (!Number.isFinite(mantissa)) {
    return formatScientificHugeValue(absValue, policy);
  }
  return `${formatMantissa(mantissa, policy)}e${engineeringExponent}`;
}

function formatStandardSuffixHugeValue(
  absValue: HugeNumber,
  policy: NumberFormatPolicy,
): string {
  const rawExponent = Math.floor(absValue.log10().toNumber());
  if (!Number.isFinite(rawExponent)) {
    return formatScientificHugeValue(absValue, policy);
  }
  if (rawExponent >= (STANDARD_SUFFIXES.length + 2) * 3) {
    return formatScientificHugeValue(absValue, policy);
  }
  const tierIndex = Math.floor(rawExponent / 3) - 2;
  if (tierIndex < 0) {
    return formatScientificHugeValue(absValue, policy);
  }
  const clamped = Math.min(tierIndex, STANDARD_SUFFIXES.length - 1);
  const suffix = STANDARD_SUFFIXES[clamped];
  const tierExponent = (clamped + 2) * 3;
  const mantissa = absValue.div(huge(`1e${tierExponent}`)).toNumber();
  if (!Number.isFinite(mantissa)) {
    return formatScientificHugeValue(absValue, policy);
  }
  return `${formatMantissa(mantissa, policy)}${suffix}`;
}

function formatMantissa(value: number, policy: NumberFormatPolicy): string {
  const digits = policy.mantissaStepped
    ? computeSteppedDigits(Math.abs(value))
    : policy.decimals;
  const fixed = value.toFixed(digits);
  if (!policy.trimTrailingZeros) return fixed;
  if (!fixed.includes(".")) return fixed;
  return fixed.replace(/\.?0+$/, "");
}

// Player-facing stepped digit count: 2 below 10, 1 below 100, 0 at 100+.
// Used by both the suffix/notation mantissa path and the locale fallback
// path. The two responsibilities are gated by separate policy flags
// (mantissaStepped vs. localeFallbackStepped) so detail/debug can opt out.
function computeSteppedDigits(absValue: number): number {
  if (!Number.isFinite(absValue)) return 0;
  if (absValue >= 100) return 0;
  if (absValue >= 10) return 1;
  return 2;
}

function formatLocaleFallback(
  absValue: number,
  policy: NumberFormatPolicy,
): string {
  if (absValue >= 1 && Number.isInteger(absValue)) {
    return formatLocaleInteger(absValue);
  }
  const digits = policy.localeFallbackStepped
    ? computeSteppedDigits(absValue)
    : policy.decimals > 0
      ? Math.min(policy.decimals, 3)
      : 0;
  if (digits === 0) {
    return formatLocaleInteger(Math.round(absValue));
  }
  const formatted = formatDecimal(absValue, digits);
  if (!policy.trimTrailingZeros) return formatted;
  if (!formatted.includes(".") && !formatted.includes(",")) return formatted;
  if (policy.localeFallbackStepped) {
    // Supported locales (ja, en) use "." as the decimal separator, so the
    // mantissa-style trim is safe here. If new locales appear, gate the trim
    // on the active locale before extending.
    return formatted.replace(/\.?0+$/, "");
  }
  // Intl は locale により小数点が "," の場合があるため、末尾 0 trim はせず保持
  return formatted;
}
